import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, isPast, parseISO } from 'date-fns';
import { Calendar, Clock, User, Mail, CalendarX, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import RescheduleModal from '@/components/booking/RescheduleModal';

interface BookingHistoryProps {
  hostUsername?: string;
}

interface Booking {
  id: string;
  user_id: string;
  booking_link_id: string;
  name: string;
  email: string;
  date: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  price?: number;
  created_at: string;
}

const BookingHistory: React.FC<BookingHistoryProps> = ({ hostUsername }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [bookingToReschedule, setBookingToReschedule] = useState<Booking | null>(null);
  
  const { data: bookings, isLoading, error, refetch } = useQuery({
    queryKey: ['bookings', hostUsername || user?.id],
    queryFn: async () => {
      // Get all bookings in a single query with proper filtering
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('user_id', user?.id)
        .order('start_time', { ascending: true });
        
      if (error) {
        throw error;
      }
      
      return data as Booking[];
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60, // Consider data fresh for 1 minute
    cacheTime: 1000 * 60 * 5, // Cache data for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    retry: 1, // Only retry once on failure
  });

  const formatBookingDate = (date: string, startTime: string) => {
    const bookingDate = parseISO(`${date}T${startTime}`);
    return format(bookingDate, 'PPP'); // Long date format
  };

  const formatBookingTime = (startTime: string, endTime: string) => {
    return `${startTime.substring(0, 5)} - ${endTime.substring(0, 5)}`;
  };

  const cancelBooking = async (bookingId: string) => {
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status: 'cancelled' })
        .eq('id', bookingId);
        
      if (error) {
        throw error;
      }
      
      toast.success('Booking cancelled successfully');
      refetch();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking');
    }
  };

  const openRescheduleModal = (booking: Booking) => {
    setBookingToReschedule(booking);
  };

  const filteredBookings = bookings?.filter(booking => {
    const isUpcoming = !isPast(parseISO(`${booking.date}T${booking.start_time}`));
    return activeTab === 'upcoming' ? isUpcoming : !isUpcoming;
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Booking History</CardTitle>
          <CardDescription>Loading your bookings...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Booking History</CardTitle>
          <CardDescription>Error loading bookings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-destructive mb-4">Failed to load bookings</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Booking History</CardTitle>
        <CardDescription>View and manage your bookings</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past">Past</TabsTrigger>
          </TabsList>
          <TabsContent value={activeTab}>
            {filteredBookings?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No {activeTab} bookings found
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBookings?.map((booking) => (
                  <div
                    key={booking.id}
                    className="flex items-center justify-between p-4 rounded-lg border"
                  >
                    <div className="flex items-center space-x-4">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{booking.name}</p>
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          <span>{formatBookingDate(booking.date, booking.start_time)}</span>
                          <span>•</span>
                          <span>{formatBookingTime(booking.start_time, booking.end_time)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={booking.status === 'confirmed' ? 'default' : 'destructive'}>
                        {booking.status}
                      </Badge>
                      {booking.status === 'confirmed' && !isPast(parseISO(`${booking.date}T${booking.start_time}`)) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openRescheduleModal(booking)}
                        >
                          <CalendarX className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      {bookingToReschedule && (
        <RescheduleModal
          booking={bookingToReschedule}
          onClose={() => setBookingToReschedule(null)}
          onSuccess={() => {
            refetch();
            setBookingToReschedule(null);
          }}
        />
      )}
    </Card>
  );
};

export default BookingHistory;
